import { SelfDrivingService } from "../../server/api/self-driving";
import { toSlug } from "../../utils/create-slug";
import type { UUID } from "../../utils/uuid";
import { trackContentGenerationResult } from "../content-generation/track-content-generation";
import type { AccountContext } from "../generic/hooks/use-account-context";

export async function getToneText(
  tone: "professional" | "casual" | "direct" | "custom",
  toneSummary: string,
  {
    format,
    description,
    fileId,
    salutation,
    signOff,
    context,
  }: {
    format: string;
    description: string;
    fileId?: string;
    salutation?: string;
    signOff?: string;
    context?: AccountContext;
  },
  token: string,
  contentGenerationId?: UUID | null,
): Promise<
  | {
      data: {
        text: string;
        contentGenerationId: UUID | null;
        contentGenerationResultId: UUID | null;
      };
      error: null;
    }
  | { data: null; error: Error }
> {
  const service = new SelfDrivingService(token);
  const formatId = toSlug(format).replace(/-/g, "_");

  try {
    const response = await service.text(formatId, {
      description,
      tone,
      toneSummary,
      salutation,
      signOff,
      fileId,
      context: {
        companyName: context?.companyName ?? undefined,
        industry: context?.industry ?? undefined,
        companyDescription: context?.companyDescription ?? undefined,
        companyValues: context?.companyValues ?? undefined,
        companyTeamReference: context?.companyTeamReference ?? undefined,
      },
    });

    if (response?.error || !response.data) {
      throw new Error(response.error);
    }

    const data = response.data;

    let contentGenerationResultId: UUID | null = null;
    if (contentGenerationId) {
      contentGenerationResultId = await trackContentGenerationResult(
        token,
        contentGenerationId,
        data,
      );
    }

    return {
      data: {
        ...data,
        contentGenerationId: contentGenerationId ?? null,
        contentGenerationResultId,
      },
      error: null,
    };
  } catch (error) {
    return { data: null, error: error instanceof Error ? error : new Error(String(error)) };
  }
}
